// ServiceRequestDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../styles/signature-canvas.css';
import assetService, { Asset } from '../services/asset.service';
import api from '../services/api';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Divider,
  alpha,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Alert,
  Tooltip,
  AlertTitle,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter
} from '@mui/material';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Inventory as InventoryIcon,
  Assignment as AssignmentIcon,
  Flag as FlagIcon,
  AttachMoney as AttachMoneyIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
  TimerOff as TimerOffIcon,
  Alarm as AlarmIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  CameraAlt as CameraAltIcon,
  Build as BuildIcon,
  Gesture as GestureIcon,
  CheckBox as CheckBoxIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import AuthLayout from '../components/AuthLayout';
import serviceRequestService from '../services/serviceRequest.service';
import { useAuth } from '../contexts/AuthContext';
import MapComponent from '../components/MapComponent';
import costService from '../services/cost.service';
import { CostType, COST_CATEGORY_LABELS, COST_TYPE_LABELS } from '../types/cost.types';
import CostPieChart from '../components/CostPieChart';
import slaService, { SLA } from '../services/sla.service';
import SignatureCanvas from 'react-signature-canvas';
import { v4 as uuidv4 } from 'uuid';
import PhotoGallery from '../components/PhotoGallery';
import SimplePhotoGallery from '../components/SimplePhotoGallery';

// Define TabPanel component for the tabs
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      style={{ overflowX: 'hidden' }}
      {...other}
    >
      {value === index && (
        <Paper
          elevation={1}
          sx={{
            p: 3,
            borderRadius: '0 0 4px 4px',
            minHeight: '300px',
            position: 'relative',
            mb: 8,
            overflowX: 'auto',
            maxHeight: 'calc(100vh - 350px)',
            '&::-webkit-scrollbar': {
              width: '8px',
              backgroundColor: '#f5f5f5'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#ddd',
              borderRadius: '4px'
            }
          }}
        >
          {children}
        </Paper>
      )}
    </div>
  );
}

const priorityColors = {
  low: '#8bc34a',
  medium: '#ffc107',
  high: '#ff9800',
  critical: '#f44336'
};

const priorityLabels = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  critical: 'Crítica'
};

const typeLabels = {
  maintenance: 'Manutenção',
  installation: 'Instalação',
  inspection: 'Inspeção',
  repair: 'Reparo',
  other: 'Outro'
};

const checklistItemTypeLabels = {
  simple_check: 'Verificação Simples',
  image_capture: 'Captura de Imagem',
  update_data: 'Atualização de Dados',
  update_equipment: 'Atualização de Equipamento',
  collect_signature: 'Coleta de Assinatura'
};

const statusLabels = {
  waiting: 'Aguardando',
  in_progress: 'Em Execução',
  completed: 'Finalizado',
  cancelled: 'Cancelado'
};

const statusColors = {
  waiting: '#f44336',
  in_progress: '#ff9800',
  completed: '#4caf50',
  cancelled: '#9e9e9e'
};

const serviceTypeLabels = {
  maintenance: 'Manutenção',
  installation: 'Instalação',
  inspection: 'Inspeção',
  repair: 'Reparo',
  other: 'Outro'
};

// Labels para status de ações em português
const actionStatusLabels = {
  info: 'Informação',
  warning: 'Aviso',
  error: 'Erro',
  success: 'Sucesso'
};

// Função única para obter o label do tipo de item
const getItemTypeLabel = (type?: string) => {
  return type
    ? (checklistItemTypeLabels[type as keyof typeof checklistItemTypeLabels] ?? type)
    : 'Desconhecido';
};

const ServiceRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [notification, setNotification] = useState<{ message: string, type: 'success' | 'error' | 'info' | 'warning' } | null>(null);

  const [loading, setLoading] = useState(true);
  // @ts-ignore - ServiceRequestType não encontrado
  const [serviceRequest, setServiceRequest] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [slaDetails, setSlaDetails] = useState<SLA | null>(null);
  const [slaLoading, setSlaLoading] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState(0);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionText, setActionText] = useState('');
  const [actionStatus, setActionStatus] = useState<'info' | 'warning' | 'error' | 'success'>('info');
  const [addingAction, setAddingAction] = useState(false);
  const [assetDetails, setAssetDetails] = useState<Asset | null>(null);
  const [costs, setCosts] = useState<CostType[]>([]);
  const [loadingCosts, setLoadingCosts] = useState(false);
  const [costsError, setCostsError] = useState<string | null>(null);
  const [totalCostsAmount, setTotalCostsAmount] = useState(0);

  // Signature state
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signatureData, setSignatureData] = useState({
    name: '',
    role: '',
    signature: null as any
  });
  const [signatureError, setSignatureError] = useState('');
  const signatureRef = React.useRef<SignatureCanvas>(null);

  // Estado para controlar o diálogo de confirmação do template de checklist
  const [confirmTemplateDialogOpen, setConfirmTemplateDialogOpen] = useState(false);

  useEffect(() => {
    loadServiceRequest();
  }, [id]);

  useEffect(() => {
    console.log('[ServiceRequestDetail] serviceRequest atualizado:', serviceRequest);
    console.log('[ServiceRequestDetail] serviceRequest.asset:', serviceRequest?.asset);
    
    if (serviceRequest?.asset && typeof serviceRequest.asset !== 'string') {
      console.log('[ServiceRequestDetail] Asset é objeto, ID:', serviceRequest.asset._id);
      console.log('[ServiceRequestDetail] Asset completo do SR:', serviceRequest.asset);
      // Se o asset já veio populado do backend, usar diretamente
      // @ts-ignore
      setAssetDetails(serviceRequest.asset);
    } else if (serviceRequest?.asset && typeof serviceRequest.asset === 'string') {
      console.log('[ServiceRequestDetail] Asset é string, carregando:', serviceRequest.asset);
      loadAssetDetails(serviceRequest.asset);
    }

    // Carregar os custos associados ao chamado técnico
    if (serviceRequest && serviceRequest._id) {
      loadCosts(serviceRequest._id);
    }

    // Verificar se deve exibir o diálogo de confirmação para carregar o template de checklist
    if (serviceRequest && (!serviceRequest.checklist || serviceRequest.checklist.length === 0) &&
      serviceRequest.suggestedChecklist && serviceRequest.suggestedChecklist.length > 0) {
      // Abrir o diálogo de confirmação
      setConfirmTemplateDialogOpen(true);
    }
  }, [serviceRequest]);

  const loadServiceRequest = async () => {
    try {
      setLoading(true);
      setError('');
      setSlaLoading(true);
      
      console.log(`Carregando dados do chamado ${id}...`);
      const data = await serviceRequestService.getServiceRequestById(id!);
      
      // Verificar os dados recebidos do backend
      console.log('Dados recebidos do backend:', JSON.stringify(data));
      
      // Verificar especificamente o checklist
      // @ts-ignore - Usar any para evitar erros de TypeScript
      if (data && (data as any).checklist && Array.isArray((data as any).checklist)) {
        // @ts-ignore - Usar any para evitar erros de TypeScript
        console.log('Checklist recebido do backend:', JSON.stringify((data as any).checklist));
        
        // Verificar se há itens com parentItemId
        // @ts-ignore - Usar any para evitar erros de TypeScript
        const itemsWithParentId = (data as any).checklist.filter((item: any) => item.parentItemId);
        console.log('Itens com parentItemId:', itemsWithParentId.length);
        
        if (itemsWithParentId.length > 0) {
          console.log('Exemplo de item com parentItemId:', itemsWithParentId[0]);
        }
      }
      
      // Verificar se há ações no chamado
      // @ts-ignore - Usar any para evitar erros de TypeScript
      const actions = (data as any).actions;
      
      if (actions && Array.isArray(actions)) {
        console.log(`Chamado carregado com ${actions.length} ações`);
        
        // Ordenar ações por data (mais recentes primeiro)
        // @ts-ignore - Usar any para evitar erros de TypeScript
        actions.sort((a: any, b: any) => {
          const dateA = a.timestamp || a.createdAt || new Date(0);
          const dateB = b.timestamp || b.createdAt || new Date(0);
          return new Date(dateB).getTime() - new Date(dateA).getTime();
        });
        
        // Exibir as últimas ações no console para depuração
        if (actions.length > 0) {
          console.log('Ações mais recentes:');
          actions.slice(0, 3).forEach((action: any, index: number) => {
            console.log(`${index + 1}. ${action.description} (${action.status}) - ${new Date(action.timestamp || action.createdAt).toLocaleString()}`);
          });
        }
      } else {
        console.log(`Chamado carregado sem ações ou com formato inválido`);
      }
      
      setServiceRequest(data);
      
      // Carregar detalhes do SLA se o chamado tiver um SLA associado
      // @ts-ignore - Propriedade sla pode não existir no tipo
      if (data.sla) {
        try {
          console.log('Carregando detalhes do SLA...');
          // @ts-ignore - Propriedade sla pode não existir no tipo
          const slaId = typeof data.sla === 'object' ? data.sla._id : data.sla;
          
          if (slaId) {
            console.log(`Buscando SLA com ID: ${slaId}`);
            const slaData = await slaService.getSLAById(slaId);
            console.log('Detalhes do SLA carregados:', slaData);
            setSlaDetails(slaData);
          } else {
            console.log('Chamado não tem ID de SLA válido');
            setSlaDetails(null);
          }
        } catch (slaError: any) {
          console.error('Erro ao carregar detalhes do SLA:', slaError);
          setSlaDetails(null);
        } finally {
          setSlaLoading(false);
        }
      } else {
        console.log('Chamado não tem SLA associado');
        setSlaDetails(null);
        setSlaLoading(false);
      }
      
      setSuccess('Chamado atualizado com sucesso');

      // Verificar se o chamado tem um checklist e se ele está vazio
      // @ts-ignore - Propriedades não existem no tipo
      if (data.type && (!data.checklist || data.checklist.length === 0) && data.suggestedChecklist && data.suggestedChecklist.length > 0) {
        setConfirmTemplateDialogOpen(true);
      }
    } catch (error: any) {
      console.error('Erro ao carregar chamado técnico:', error);
      setError(error.response?.data?.message || 'Erro ao carregar chamado técnico');
    } finally {
      setLoading(false);
    }
  };

  const loadAssetDetails = async (assetId: string) => {
    try {
      console.log('[ServiceRequestDetail] Carregando asset:', assetId);
      const response = await assetService.getAssetById(assetId);
      console.log('[ServiceRequestDetail] Resposta do asset:', response);
      
      if (response.asset) {
        console.log('[ServiceRequestDetail] Asset recebido:', response.asset);
        console.log('[ServiceRequestDetail] Asset latitude:', (response.asset as any).latitude);
        console.log('[ServiceRequestDetail] Asset longitude:', (response.asset as any).longitude);
        // @ts-ignore - Incompatibilidade de tipos
        setAssetDetails(response.asset);
      }
    } catch (error) {
      console.error('Erro ao carregar detalhes do ativo:', error);
    }
  };

  // Função para carregar os custos associados ao chamado técnico
  const loadCosts = async (serviceRequestId: string) => {
    try {
      setLoadingCosts(true);
      setCostsError(null);

      const response = await costService.getCostsByServiceRequest(serviceRequestId);

      if (response && response.costs) {
        setCosts(response.costs);

        // Calcular o total dos custos
        const total = response.costs.reduce((sum: number, cost: any) => sum + (cost.amount || 0), 0);
        setTotalCostsAmount(total);
      } else {
        setCosts([]);
        setTotalCostsAmount(0);
      }
    } catch (error) {
      console.error('Erro ao carregar custos do chamado:', error);
      setCostsError('Não foi possível carregar os custos associados a este chamado.');
      setCosts([]);
      setTotalCostsAmount(0);
    } finally {
      setLoadingCosts(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDeleteClick = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await serviceRequestService.deleteServiceRequest(id!);
      navigate('/service-requests');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao cancelar chamado técnico');
    } finally {
      setConfirmDeleteOpen(false);
    }
  };

  const handleAddAction = async () => {
    if (!actionText.trim()) return;

    setAddingAction(true);
    try {
      await serviceRequestService.addAction(id!, actionText, actionStatus);
      setActionText('');
      setActionStatus('info');
      loadServiceRequest();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao adicionar ação');
    } finally {
      setAddingAction(false);
      setActionDialogOpen(false);
    }
  };

  // Função para obter o ícone de acordo com o tipo do item
  const getItemTypeIcon = (type: string) => {
    switch (type) {
      case 'image_capture':
        return <CameraAltIcon fontSize="small" color="primary" />;
      case 'update_data':
        return <EditIcon fontSize="small" color="info" />;
      case 'update_equipment':
        return <BuildIcon fontSize="small" color="warning" />;
      case 'collect_signature':
        return <GestureIcon fontSize="small" color="secondary" />;
      default:
        return <CheckBoxIcon fontSize="small" color="action" />;
    }
  };

  // Função para obter a cor do tipo de item
  const getItemTypeColor = (type?: string) => {
    switch (type) {
      case 'image_capture':
        return '#2196f3'; // Azul
      case 'update_data':
        return '#ff9800'; // Laranja
      case 'update_equipment':
        return '#9c27b0'; // Roxo
      case 'collect_signature':
        return '#f44336'; // Vermelho
      case 'simple_check':
      default:
        return '#4caf50'; // Verde
    }
  };

  // Função para lidar com a abertura do diálogo de assinatura
  const handleOpenSignatureDialog = () => {
    setSignatureDialogOpen(true);
    setSignatureData({
      name: '',
      role: 'Cliente',
      signature: null
    });
    setSignatureError('');
  };

  // Função para lidar com o fechamento do diálogo de assinatura
  const handleCloseSignatureDialog = () => {
    setSignatureDialogOpen(false);
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  // Função para limpar a assinatura
  const handleClearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setSignatureData(prev => ({ ...prev, signature: null }));
    }
  };

  // Função para salvar a assinatura
  const handleSaveSignature = async () => {
    if (!signatureData.name.trim()) {
      setSignatureError('Por favor, informe o nome do signatário');
      return;
    }

    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setSignatureError('Por favor, forneça uma assinatura');
      return;
    }

    try {
      // Obter a assinatura como uma imagem em base64
      if (!signatureRef.current) {
        setSignatureError('Erro: Referência ao canvas de assinatura não encontrada');
        return;
      }

      const signatureDataUrl = signatureRef.current.toDataURL('image/png');

      try {
        const response = await fetch(signatureDataUrl);
        const blob = await response.blob();
        const timestamp = new Date().getTime();
        const file = new File([blob], `signature-${timestamp}.png`, { type: 'image/png' });

        // Fazer upload da assinatura para o servidor
        const formData = new FormData();
        formData.append('file', file);

        const uploadResponse = await api.post('/api/upload/signature', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Adicionar a assinatura ao chamado
        if (uploadResponse.data && uploadResponse.data.url) {
          const signatureUrl = uploadResponse.data.url;

          await serviceRequestService.addSignatures(id!, [{
            url: signatureUrl,
            name: signatureData.name,
            role: signatureData.role
          }]);

          // Recarregar os dados do chamado
          await loadServiceRequest();

          // Fechar o diálogo e limpar o formulário
          handleCloseSignatureDialog();

          // Mostrar mensagem de sucesso
          setNotification({
            message: 'Assinatura adicionada com sucesso!',
            type: 'success'
          });
        } else {
          throw new Error('URL da assinatura não retornada pelo servidor');
        }
      } catch (uploadError: any) {
        console.error('Erro ao fazer upload da assinatura:', uploadError);
        setSignatureError(`Erro ao fazer upload: ${uploadError.message || 'Erro desconhecido'}`);
      }
    } catch (error: any) {
      console.error('Erro ao salvar assinatura:', error);
      setSignatureError(`Erro ao salvar assinatura: ${error.message || 'Erro desconhecido'}. Por favor, tente novamente.`);
    }
  };

  // Função para alternar o estado de um item do checklist
  const handleToggleChecklistItem = async (item: any) => {
    try {
      const updatedItem = { ...item, isCompleted: !item.isCompleted };
      const updatedChecklist = serviceRequest!.checklist.map((checkItem: any) =>
        checkItem._id === item._id ? updatedItem : checkItem
      );

      setLoading(true);
      await serviceRequestService.updateChecklist(id!, updatedChecklist);
      loadServiceRequest();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao atualizar item do checklist');
    } finally {
      setLoading(false);
    }
  };

  // Função para aplicar o template de checklist ao chamado
  const applyChecklistTemplate = async () => {
    if (!serviceRequest) return;

    try {
      setLoading(true);

      // Usar o novo método applyChecklistTemplatesDirect que busca os templates e aplica diretamente
      const result = await serviceRequestService.applyChecklistTemplatesDirect(id!, serviceRequest.type);

      if (result && result.checklist && Array.isArray(result.checklist) && result.checklist.length > 0) {
        // Atualizar o estado do serviceRequest com o novo checklist
        setServiceRequest((prevState: any) => {
          const updatedState = {
            ...prevState!,
            checklist: result.checklist
          };
          return updatedState;
        });

        // Exibir notificação de sucesso
        setNotification({
          message: `Checklist inicializado com ${result.checklist.length} itens!`,
          type: 'success'
        });

        // Atualizar a aba para a aba de checklist
        setTabValue(1);
      } else {
        // Exibir notificação de aviso
        setNotification({
          message: result.message || 'Nenhum template de checklist disponível para este tipo de chamado',
          type: 'warning'
        });
      }

      setTimeout(() => setNotification(null), 3000);
    } catch (error: any) {
      console.error('Erro ao aplicar template de checklist:', error);
      setError(error.response?.data?.message || 'Erro ao inicializar checklist');

      setNotification({
        message: 'Erro ao inicializar checklist',
        type: 'error'
      });

      setTimeout(() => setNotification(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // A função e o estado para adição de item ao checklist foram removidos conforme requisito

  // Render
  if (loading) {
    return (
      <AuthLayout title="Carregando Chamado...">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </AuthLayout>
    );
  }

  if (error) {
    return (
      <AuthLayout title="Erro">
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
          <Button
            variant="contained"
            onClick={() => navigate('/service-requests')}
            sx={{ mt: 2 }}
          >
            Voltar para Lista
          </Button>
        </Box>
      </AuthLayout>
    );
  }

  if (!serviceRequest) {
    return (
      <AuthLayout title={`Chamado ${serviceRequest.number || ''}`}>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <AlertTitle>Sucesso</AlertTitle>
            {success}
          </Alert>
        )}
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">Chamado técnico não encontrado</Alert>
          <Button
            variant="contained"
            onClick={() => navigate('/service-requests')}
            sx={{ mt: 2 }}
          >
            Voltar para Lista
          </Button>
        </Box>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Detalhes do Chamado">
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        {notification && (
          <Alert
            severity={notification.type}
            sx={{
              position: 'fixed',
              top: 16,
              right: 16,
              zIndex: 9999,
              boxShadow: 3
            }}
            onClose={() => setNotification(null)}
          >
            {notification.message}
          </Alert>
        )}

        <Box sx={{ p: 3, pb: 8 }}>
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link color="inherit" onClick={() => navigate('/service-requests')} sx={{ cursor: 'pointer' }}>
              Chamados Técnicos
            </Link>
            <Typography color="text.primary">
              {serviceRequest.number}
            </Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h1">
              Detalhes do Chamado Técnico
            </Typography>

            <Box>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/service-requests')}
                sx={{ mr: 1 }}
              >
                Voltar
              </Button>

              <Button
                variant="outlined"
                color="primary"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/service-requests/edit/${id}`)}
                sx={{ mr: 1 }}
              >
                Editar
              </Button>

              {serviceRequest.status !== 'cancelled' && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteClick}
                >
                  Cancelar
                </Button>
              )}
            </Box>
          </Box>

          {/* Informações principais do chamado */}
          <Paper elevation={1} sx={{ mb: 3, p: 3 }}>
            <Grid container spacing={3}>
              {/* Coluna da esquerda - Informações principais */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AssignmentIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Número do Chamado
                      </Typography>
                    </Box>
                    <Typography variant="h6">
                      {serviceRequest.number}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <FlagIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                    </Box>
                    <Chip
                      label={statusLabels[serviceRequest.status as keyof typeof statusLabels]}
                      sx={{
                        backgroundColor: statusColors[serviceRequest.status as keyof typeof statusColors],
                        color: '#fff',
                        fontWeight: 'bold'
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Cliente
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {serviceRequest.client && typeof serviceRequest.client === 'object'
                        ? serviceRequest.client.name
                        : 'Cliente não encontrado'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <InventoryIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Ativo
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {serviceRequest.asset && typeof serviceRequest.asset === 'object'
                        ? serviceRequest.asset.name
                        : 'Ativo não encontrado'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AssignmentIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Tipo
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {typeLabels[serviceRequest.type as keyof typeof typeLabels]}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <FlagIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Prioridade
                      </Typography>
                    </Box>
                    <Chip
                      label={priorityLabels[serviceRequest.priority as keyof typeof priorityLabels]}
                      sx={{
                        backgroundColor: priorityColors[serviceRequest.priority as keyof typeof priorityColors],
                        color: '#fff',
                        fontWeight: 'bold'
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Descrição
                      </Typography>
                    </Box>
                    <Paper elevation={0} sx={{
                      p: 2,
                      bgcolor: 'background.default',
                      borderRadius: 1,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&::-webkit-scrollbar': { width: '6px', backgroundColor: '#f5f5f5' },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '4px' }
                    }}>
                      {(serviceRequest.description || '').includes('DETECÇÃO DE OBJETOS') ? (
                        <Box>
                          {(serviceRequest.description || '').split('=== DETECÇÃO DE OBJETOS').map((part: any, index: number) => {
                            if (index === 0) {
                              return (
                                <Typography key="desc-part" variant="body1" sx={{
                                  whiteSpace: 'pre-line',
                                  mb: 2,
                                  fontFamily: 'inherit',
                                  fontSize: '0.95rem',
                                  lineHeight: 1.6
                                }}>
                                  {part}
                                </Typography>
                              );
                            } else {
                              return (
                                <Box key={`yolo-part-${index}`} sx={{
                                  mt: 2,
                                  p: 2,
                                  bgcolor: 'rgba(25, 118, 210, 0.05)',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'primary.light'
                                }}>
                                  <Typography variant="subtitle2" color="primary" gutterBottom sx={{
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}>
                                    <CameraAltIcon sx={{ mr: 1, fontSize: '1rem' }} />
                                    DETECÇÃO DE OBJETOS{part.split('\n')[0]}
                                  </Typography>

                                  <Typography variant="body2" sx={{
                                    whiteSpace: 'pre-line',
                                    fontFamily: 'monospace',
                                    fontSize: '0.9rem',
                                    lineHeight: 1.8,
                                    mt: 1
                                  }}>
                                    {part.split('\n').slice(1).join('\n')}
                                  </Typography>
                                </Box>
                              );
                            }
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body1" sx={{
                          whiteSpace: 'pre-line',
                          fontFamily: 'inherit',
                          fontSize: '0.95rem',
                          lineHeight: 1.6
                        }}>
                          {serviceRequest.description}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Técnico Responsável
                      </Typography>
                    </Box>
                    {serviceRequest.assignedTo ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          src={typeof serviceRequest.assignedTo === 'object' ? serviceRequest.assignedTo.photoURL : undefined}
                          sx={{ width: 32, height: 32, mr: 1 }}
                        >
                          <PersonIcon />
                        </Avatar>
                        <Typography variant="body1">
                          {typeof serviceRequest.assignedTo === 'object'
                            ? serviceRequest.assignedTo.displayName
                            : 'Técnico não encontrado'}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body1" color="text.secondary">
                        Não atribuído
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Tempo Estimado
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {serviceRequest.estimatedTime
                        ? `${serviceRequest.estimatedTime} minutos`
                        : 'Não especificado'}
                    </Typography>
                  </Grid>

                  {serviceRequest.notes && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Observações
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                        {serviceRequest.notes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {/* Coluna da direita - Total de Custos e outras informações */}
              <Grid item xs={12} md={4}>
                {/* Widget de SLA */}
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 3, 
                    mb: 3, 
                    borderRadius: 2,
                    bgcolor: theme => alpha(theme.palette.primary.light, 0.05),
                    border: '1px solid',
                    borderColor: theme => theme.palette.primary.light
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      Contrato SLA
                    </Typography>
                    {slaLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Chip 
                        size="small" 
                        label={slaDetails?.name || 'Não definido'} 
                        color="primary" 
                        variant="outlined" 
                      />
                    )}
                  </Box>
                  
                  {slaLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : slaDetails ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <TimerIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1rem' }} />
                        <Typography variant="body2">
                          Tempo de resposta: <strong>{slaDetails.responseTime}h</strong>
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AlarmIcon sx={{ mr: 1, color: 'secondary.main', fontSize: '1rem' }} />
                        <Typography variant="body2">
                          Tempo de resolução: <strong>{slaDetails.resolutionTime}h</strong>
                        </Typography>
                      </Box>
                      
                      {/* @ts-ignore - Propriedade slaDetails pode não existir no tipo */}
                      {serviceRequest.slaDetails && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <AccessTimeIcon sx={{ mr: 1, color: 'info.main', fontSize: '1rem' }} />
                            <Typography variant="body2">
                              Prazo de resposta: <strong>
                                {/* @ts-ignore - Propriedade slaDetails pode não existir no tipo */}
                                {serviceRequest.slaDetails.responseDeadline 
                                  ? format(new Date(serviceRequest.slaDetails.responseDeadline), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
                                  : 'Não definido'}
                              </strong>
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {/* @ts-ignore - Propriedade slaDetails pode não existir no tipo */}
                            <SpeedIcon sx={{ mr: 1, color: serviceRequest.slaDetails.isOverdue ? 'error.main' : 'success.main', fontSize: '1rem' }} />
                            <Typography variant="body2">
                              Prazo de resolução: <strong>
                                {/* @ts-ignore - Propriedade slaDetails pode não existir no tipo */}
                                {serviceRequest.slaDetails.resolutionDeadline 
                                  ? format(new Date(serviceRequest.slaDetails.resolutionDeadline), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
                                  : 'Não definido'}
                              </strong>
                            </Typography>
                          </Box>
                          
                          {/* @ts-ignore - Propriedade slaDetails pode não existir no tipo */}
                          {serviceRequest.slaDetails.isOverdue && (
                            <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                              <Typography variant="caption">Prazo excedido!</Typography>
                            </Alert>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2">SLA não configurado para este chamado.</Typography>
                    </Alert>
                  )}
                </Paper>
                
                <Paper
                  elevation={1}
                  sx={{
                    p: 3,
                    mb: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    height: 'auto'
                  }}
                >
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Total de Custos do Chamado
                  </Typography>
                  {loadingCosts ? (
                    <CircularProgress size={30} sx={{ my: 2 }} />
                  ) : (
                    <>
                      <Typography variant="h4" color="primary" fontWeight="bold">
                        {costService.formatCurrency(totalCostsAmount)}
                      </Typography>
                      {costs.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                          {costs.length} {costs.length === 1 ? 'custo registrado' : 'custos registrados'}
                        </Typography>
                      )}
                    </>
                  )}
                </Paper>

                {/* Quadro de Forecast de Custos */}
                <Paper
                  elevation={1}
                  sx={{
                    p: 3,
                    mb: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    height: 'auto',
                    borderLeft: '4px solid #3f51b5'
                  }}
                >
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Forecast de Custos
                  </Typography>
                  <Typography variant="h4" color="secondary" fontWeight="bold">
                    {serviceRequest.checklist && serviceRequest.checklist.length > 0 ?
                      costService.formatCurrency(
                        serviceRequest.checklist
                          .reduce((total: number, item: any) => total + (Number(item.value) || 0), 0)
                      ) :
                      costService.formatCurrency(0)
                    }
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    {serviceRequest.checklist && serviceRequest.checklist.length > 0 ?
                      `${serviceRequest.checklist.length} ${serviceRequest.checklist.length === 1 ? 'item no checklist' : 'itens no checklist'}` :
                      'Nenhum item no checklist'
                    }
                  </Typography>
                </Paper>

                {/* Gráfico de Pizza dos Principais Custos */}
                {!loadingCosts &&
                  <CostPieChart costs={costs} totalCostsAmount={totalCostsAmount} />
                }
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Paper
              elevation={1}
              sx={{
                mb: 0,
                borderRadius: '4px 4px 0 0',
                position: 'sticky',
                top: 64,
                backgroundColor: 'background.paper',
                zIndex: 2
              }}
            >
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="service request tabs"
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    py: 1.5,
                    px: 3,
                    fontWeight: 'medium',
                    textTransform: 'none',
                    minWidth: 120,
                    fontSize: '0.9rem'
                  },
                  '& .Mui-selected': {
                    fontWeight: 'bold',
                    color: 'primary.main'
                  }
                }}
              >
                <Tab label="Histórico de Ações" />
                <Tab label="Checklist" />
                <Tab label="Fotos" />
                <Tab label="Assinaturas" />
                <Tab label="Localização" />
                <Tab label="Custos" />
                <Tab label="Forecast de Custos" />
              </Tabs>
            </Paper>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, position: 'sticky', top: 0, zIndex: 5, pt: 1, pb: 1, backgroundColor: 'white' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadServiceRequest}
                sx={{ boxShadow: 1 }}
              >
                Atualizar Histórico
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setActionDialogOpen(true)}
                sx={{ boxShadow: 2 }}
              >
                Adicionar Ação
              </Button>
            </Box>

            {serviceRequest.actions && serviceRequest.actions.length > 0 ? (
              <Box>
                {/* Ordenar as ações por data de criação (mais recentes primeiro) */}
                {[...serviceRequest.actions]
                  .sort((a, b) => {
                    // Usar timestamp, createdAt ou qualquer data disponível
                    const dateA = a.timestamp || a.createdAt || new Date(0);
                    const dateB = b.timestamp || b.createdAt || new Date(0);
                    return new Date(dateB).getTime() - new Date(dateA).getTime();
                  })
                  .map((action: any, index: number) => (
                  <Paper
                    key={action._id || index}
                    elevation={1}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderLeft: `4px solid ${
                        action.status === 'success' ? '#4caf50' :
                          action.status === 'error' ? '#f44336' :
                            action.status === 'warning' ? '#ff9800' : '#2196f3'
                      }`
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="subtitle1">
                          {action.title || `Ação ${index + 1}`}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                          {action.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Status: {actionStatusLabels[action.status as keyof typeof actionStatusLabels] || actionStatusLabels.info}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {action.createdAt && !isNaN(new Date(action.createdAt).getTime())
                              ? format(new Date(action.createdAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })
                              : 'Data não disponível'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                Nenhuma ação registrada
              </Typography>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {/* O botão de adicionar item foi removido conforme requisito */}

            {serviceRequest.checklist && serviceRequest.checklist.length > 0 ? (
              <Box>
                {(() => {
                  // Função para obter cor baseada no nível
                  const getHierarchyColor = (level: number): string => {
                    const colors = [
                      '#ff9800', // Laranja - nível 0 (itens principais)
                      '#2196f3', // Azul - nível 1
                      '#4caf50', // Verde - nível 2
                      '#9c27b0', // Roxo - nível 3
                      '#e91e63', // Rosa - nível 4
                      '#607d8b'  // Cinza azulado - nível 5+
                    ];
                    return colors[Math.min(level, colors.length - 1)];
                  };
                  
                  // Função recursiva para renderizar um item e seus filhos
                  const renderItemWithChildren = (currentItem: any, level: number = 0) => {
                    console.log(`Processando item: ${currentItem.description}, ID: ${currentItem._id}, nível: ${level}`);
                    
                    // Encontrar todos os filhos diretos deste item
                    const itemId = currentItem._id || `temp-${serviceRequest.checklist.indexOf(currentItem)}`;
                    console.log(`Buscando filhos para o item ID: ${itemId}`);
                    
                    // Log de todos os itens e seus parentItemId para debug
                    console.log('Todos os itens e seus parentItemId:');
                    serviceRequest.checklist.forEach((item: any, idx: number) => {
                      console.log(`Item ${idx}: ${item.description}, parentItemId: ${item.parentItemId}, _id: ${item._id}`);
                    });
                    
                    const children = serviceRequest.checklist.filter(
                      (child: any) => {
                        // Verificar se o parentItemId do filho corresponde ao ID do item atual
                        let isChild = false;
                        
                        if (currentItem._id && child.parentItemId === currentItem._id) {
                          isChild = true;
                          console.log(`Filho encontrado por _id: ${child.description}, parentItemId: ${child.parentItemId}, _id pai: ${currentItem._id}`);
                        } else if (child.parentItemId && child.parentItemId.startsWith('temp-')) {
                          // Se o parentItemId começa com 'temp-', extrair o índice
                          const parentIndex = parseInt(child.parentItemId.replace('temp-', ''));
                          if (parentIndex === serviceRequest.checklist.indexOf(currentItem)) {
                            isChild = true;
                            console.log(`Filho encontrado por índice: ${child.description}, parentItemId: ${child.parentItemId}, índice pai: ${serviceRequest.checklist.indexOf(currentItem)}`);
                          }
                        }
                        
                        return isChild;
                      }
                    );
                    
                    console.log(`Total de filhos encontrados: ${children.length}`);
                    if (children.length > 0) {
                      console.log('Filhos:', children.map((c: any) => c.description));
                    }
                    
                    return (
                      <React.Fragment key={currentItem._id || `item-${serviceRequest.checklist.indexOf(currentItem)}`}>
                        <Paper
                          elevation={1}
                          sx={{
                            p: 3,
                            mb: 2,
                            ml: level * 20, // Recuo baseado no nível
                            borderLeft: `4px solid ${currentItem.isCompleted ? '#4caf50' : getHierarchyColor(level)}`,
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Checkbox
                              checked={!!currentItem.isCompleted}
                              onChange={() => handleToggleChecklistItem(currentItem)}
                              sx={{ mt: -1, ml: -1 }}
                            />
                            <Box sx={{ ml: 1, width: '100%' }}>
                              {/* Header com título e valores */}
                              <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {level > 0 && (
                                    <Chip
                                      label={`NÍVEL ${level}`}
                                      size="small"
                                      sx={{
                                        mr: 1,
                                        backgroundColor: getHierarchyColor(level),
                                        color: '#fff',
                                        height: '20px',
                                        '& .MuiChip-label': { px: 1, py: 0 }
                                      }}
                                    />
                                  )}
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      textDecoration: currentItem.isCompleted ? 'line-through' : 'none',
                                      color: currentItem.isCompleted ? 'text.secondary' : 'text.primary',
                                      fontWeight: 'medium'
                                    }}
                                  >
                                    {currentItem.title || currentItem.description?.split('\n')[0] || 'Item sem título'}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  {currentItem.collectedValue && (
                                    <Chip
                                      label={currentItem.collectedValue}
                                      color="success"
                                      size="small"
                                      sx={{
                                        fontWeight: 'bold',
                                        minWidth: '80px',
                                        '& .MuiChip-label': { px: 1 }
                                      }}
                                    />
                                  )}
                                  <Chip
                                    label={currentItem.value ? costService.formatCurrency(Number(currentItem.value) || 0) : 'Sem valor'}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                      fontWeight: 'bold',
                                      minWidth: '100px',
                                      '& .MuiChip-label': { px: 1 }
                                    }}
                                  />
                                </Box>
                              </Box>

                              {/* Descrição do item */}
                              <Typography
                                variant="body2"
                                sx={{
                                  textDecoration: currentItem.isCompleted ? 'line-through' : 'none',
                                  color: currentItem.isCompleted ? 'text.secondary' : 'text.primary',
                                  whiteSpace: 'pre-line',
                                  mb: 2
                                }}
                              >
                                {currentItem.description}
                              </Typography>
                              
                              {/* Informações adicionais */}
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                  Tipo: {getItemTypeLabel(currentItem.type)}
                                </Typography>
                                {currentItem.isCompleted && (
                                  <>
                                    <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5, ml: 1, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary">
                                      Concluído em: {currentItem.completedAt ? format(new Date(currentItem.completedAt), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'Data desconhecida'}
                                    </Typography>
                                  </>
                                )}
                              </Box>
                              
                              {/* Mostrar informação do item pai, se houver */}
                              {currentItem.parentItemId && (
                                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                    Item pai: {(() => {
                                      const parentItem = serviceRequest.checklist.find(
                                        (parent: any) => parent._id === currentItem.parentItemId || 
                                                      `temp-${serviceRequest.checklist.indexOf(parent)}` === currentItem.parentItemId
                                      );
                                      return parentItem ? (parentItem.title || parentItem.description?.split('\n')[0] || 'Item sem título') : 'Desconhecido';
                                    })()}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                        
                        {/* Renderizar filhos recursivamente */}
                        {children.map((child: any) => renderItemWithChildren(child, level + 1))}
                      </React.Fragment>
                    );
                  };

                  // Renderizar apenas os itens de nível superior (sem parentItemId)
                  console.log('Todos os itens do checklist:', serviceRequest.checklist);
                  console.log('Itens com parentItemId:', serviceRequest.checklist.filter((item: any) => item.parentItemId));
                  
                  const rootItems = serviceRequest.checklist.filter((item: any) => !item.parentItemId);
                  console.log('Itens de nível superior (root items):', rootItems);
                  
                  return rootItems.map((item: any) => {
                    console.log('Renderizando item de nível superior:', item);
                    return renderItemWithChildren(item);
                  });
                })()}
              </Box>
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                Nenhum item de checklist adicionado
              </Typography>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {/* Componente de galeria de fotos */}
            <PhotoGallery
              serviceRequestId={id!}
              photos={serviceRequest.photos || []}
              onPhotosUpdated={(updatedPhotos: any[]) => {
                setServiceRequest({
                  ...serviceRequest,
                  photos: updatedPhotos
                });
              }}
              readOnly={false} /* Manter readOnly como false para garantir que o botão seja exibido */
            />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, position: 'sticky', top: 0, zIndex: 5, pt: 1, pb: 1, backgroundColor: 'white' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenSignatureDialog}
                sx={{ boxShadow: 2 }}
              >
                Adicionar Assinatura
              </Button>
            </Box>

            {serviceRequest.signatures && serviceRequest.signatures.length > 0 ? (
              <Grid container spacing={2}>
                {serviceRequest.signatures.map((signature: any, index: number) => (
                  <Grid item xs={12} sm={6} key={signature._id || index}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <img
                        src={signature.url.startsWith('http') ? signature.url : `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}${signature.url.startsWith('/') ? signature.url : `/${signature.url}`}`}
                        alt={`Assinatura de ${signature.name}`}
                        style={{ width: '100%', maxHeight: 150, objectFit: 'contain' }}
                      />
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        {signature.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {signature.role || 'Função não especificada'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {signature.timestamp
                          ? format(new Date(signature.timestamp), 'dd/MM/yyyy HH:mm', { locale: ptBR })
                          : 'Data desconhecida'}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                Nenhuma assinatura adicionada
              </Typography>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Grid container spacing={3}>
              {/* Localização de Abertura da OS */}
              {serviceRequest.openedLocation?.latitude && serviceRequest.openedLocation?.longitude && (
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon color="primary" />
                      Localização de Abertura da OS
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Coordenadas GPS
                      </Typography>
                      <Typography variant="body1">
                        Latitude: {serviceRequest.openedLocation.latitude}°
                      </Typography>
                      <Typography variant="body1">
                        Longitude: {serviceRequest.openedLocation.longitude}°
                      </Typography>
                      {serviceRequest.openedLocation.address && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {serviceRequest.openedLocation.address}
                        </Typography>
                      )}
                      {serviceRequest.openedLocation.timestamp && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Capturado em: {format(new Date(serviceRequest.openedLocation.timestamp), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ height: 350, width: '100%', borderRadius: 1, overflow: 'hidden' }}>
                      <MapComponent
                        latitude={Number(serviceRequest.openedLocation.latitude)}
                        longitude={Number(serviceRequest.openedLocation.longitude)}
                        popupText="Local de Abertura da OS"
                      />
                    </Box>
                  </Paper>
                </Grid>
              )}

              {/* Localização do Asset */}
              {((assetDetails as any)?.latitude && (assetDetails as any)?.longitude) || 
               (assetDetails?.address?.coordinates?.lat && assetDetails?.address?.coordinates?.lng) ? (
                <Grid item xs={12} md={serviceRequest.openedLocation?.latitude ? 6 : 12}>
                  <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon color="secondary" />
                      Localização do Ativo
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {assetDetails?.name}
                      </Typography>
                      
                      {/* Priorizar latitude/longitude diretas do asset */}
                      {(assetDetails as any)?.latitude && (assetDetails as any)?.longitude ? (
                        <>
                          <Typography variant="body1">
                            Latitude: {(assetDetails as any).latitude}°
                          </Typography>
                          <Typography variant="body1">
                            Longitude: {(assetDetails as any).longitude}°
                          </Typography>
                        </>
                      ) : assetDetails?.address && assetDetails.address.coordinates ? (
                        <>
                          <Typography variant="body1" sx={{ mb: 1 }}>
                            {assetDetails.address.street}, {assetDetails.address.number}
                            {assetDetails.address.complement && `, ${assetDetails.address.complement}`}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {assetDetails.address.neighborhood}, {assetDetails.address.city} - {assetDetails.address.state}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            CEP: {assetDetails.address.zipCode}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Latitude: {assetDetails.address.coordinates.lat}°
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Longitude: {assetDetails.address.coordinates.lng}°
                          </Typography>
                        </>
                      ) : null}
                    </Box>

                    <Box sx={{ height: 350, width: '100%', borderRadius: 1, overflow: 'hidden' }}>
                      <MapComponent
                        latitude={
                          (assetDetails as any)?.latitude 
                            ? Number((assetDetails as any).latitude)
                            : Number(assetDetails?.address?.coordinates?.lat || 0)
                        }
                        longitude={
                          (assetDetails as any)?.longitude
                            ? Number((assetDetails as any).longitude)
                            : Number(assetDetails?.address?.coordinates?.lng || 0)
                        }
                        popupText={assetDetails?.name || 'Localização do Ativo'}
                      />
                    </Box>
                  </Paper>
                </Grid>
              ) : null}

              {/* Mensagem quando não há nenhuma localização */}
              {!serviceRequest.openedLocation?.latitude && 
               !(assetDetails as any)?.latitude && 
               !assetDetails?.address?.coordinates?.lat && (
                <Grid item xs={12}>
                  <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    Nenhuma informação de localização disponível para este chamado.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* Dialog para captura de assinatura */}
          <Dialog
            open={signatureDialogOpen}
            onClose={handleCloseSignatureDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Adicionar Assinatura</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Nome do Signatário"
                  value={signatureData.name}
                  onChange={(e) => setSignatureData({ ...signatureData, name: e.target.value })}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  select
                  label="Função"
                  value={signatureData.role}
                  onChange={(e) => setSignatureData({ ...signatureData, role: e.target.value })}
                  margin="normal"
                >
                  <MenuItem value="Cliente">Cliente</MenuItem>
                  <MenuItem value="Técnico">Técnico</MenuItem>
                  <MenuItem value="Supervisor">Supervisor</MenuItem>
                  <MenuItem value="Outro">Outro</MenuItem>
                </TextField>

                <Box
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    mt: 2,
                    p: 1,
                    backgroundColor: '#f9f9f9'
                  }}
                >
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Assine no quadro abaixo:
                  </Typography>
                  <Box
                    sx={{
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      backgroundColor: 'white',
                      height: 200,
                      width: '100%',
                      mb: 2,
                      position: 'relative'
                    }}
                  >
                    <SignatureCanvas
                      ref={signatureRef}
                      canvasProps={{
                        width: 500,
                        height: 200,
                        className: 'signature-canvas',
                        style: {
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundColor: 'white',
                          touchAction: 'none',
                          msTouchAction: 'none',
                          cursor: 'crosshair'
                        },
                      }}
                      backgroundColor="white"
                      penColor="black"
                      minWidth={1.5}
                      maxWidth={3}
                      // @ts-ignore - Propriedade throttle não existe no tipo
                      throttle={16}
                      onEnd={() => {
                        if (signatureRef.current && !signatureRef.current.isEmpty()) {
                          setSignatureData(prev => ({
                            ...prev,
                            signature: signatureRef.current?.toDataURL('image/png')
                          }));
                        }
                      }}
                    />
                  </Box>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearSignature}
                    sx={{ mr: 1 }}
                  >
                    Limpar
                  </Button>

                  {signatureError && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                      {signatureError}
                    </Typography>
                  )}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseSignatureDialog}>Cancelar</Button>
              <Button
                onClick={handleSaveSignature}
                variant="contained"
                disabled={!signatureData.name.trim() || (signatureRef.current?.isEmpty() ?? true)}
              >
                Salvar Assinatura
              </Button>
            </DialogActions>
          </Dialog>

          <TabPanel value={tabValue} index={5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, position: 'sticky', top: 0, zIndex: 5, pt: 1, pb: 1, backgroundColor: 'white', alignItems: 'center' }}>
              <Typography variant="h6" component="div">
                Custos Diretos Técnicos
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/costs/new?serviceRequest=${id}`)}
                sx={{ boxShadow: 2 }}
              >
                Adicionar Custo
              </Button>
            </Box>

            {loadingCosts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : costsError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {costsError}
              </Alert>
            ) : costs.length > 0 ? (
              <>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <AlertTitle>Erro</AlertTitle>
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <AlertTitle>Sucesso</AlertTitle>
                    {success}
                  </Alert>
                )}
                <Paper elevation={1}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Categoria</TableCell>
                        <TableCell>Descrição</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell align="right">Data</TableCell>
                        <TableCell align="center">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {costs.map((cost) => (
                        <TableRow key={cost._id}>
                          <TableCell>
                            <Tooltip title={COST_TYPE_LABELS[cost.costType as keyof typeof COST_TYPE_LABELS]}>
                              <Chip
                                label={COST_CATEGORY_LABELS[cost.category] || cost.category}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell>{cost.description}</TableCell>
                          <TableCell align="right">{costService.formatCurrency(cost.amount)}</TableCell>
                          <TableCell align="right">
                            {format(new Date(cost.date), 'dd/MM/yyyy', { locale: ptBR })}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/costs/${cost._id}`)}
                              color="primary"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/costs/${cost._id}/edit`)}
                              color="secondary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="subtitle2">
                            Total
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                            {costService.formatCurrency(totalCostsAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell colSpan={2} />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </Paper>

                {/* Quadro com o valor total do forecast de custos */}
                <Paper elevation={1} sx={{ mt: 3, p: 2, borderLeft: '4px solid #3f51b5' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" color="text.secondary">
                        Total do Forecast de Custos
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Baseado nos itens do checklist
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h4" color="primary" fontWeight="bold">
                        {serviceRequest.checklist && serviceRequest.checklist.length > 0 ?
                          costService.formatCurrency(
                            serviceRequest.checklist
                              .reduce((total: number, item: any) => total + (Number(item.value) || 0), 0)
                          ) :
                          costService.formatCurrency(0)
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </>
            ) : (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                  <AssignmentIcon color="disabled" sx={{ fontSize: 60, mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Nenhum custo registrado
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 500 }}>
                    Este chamado técnico ainda não possui custos associados. Clique no botão abaixo para adicionar um novo custo.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(`/costs/new?serviceRequest=${id}`)}
                  >
                    Adicionar Custo
                  </Button>
                </Box>
              </Paper>
            )}
          </TabPanel>

          {/* Aba de Forecast de Custos */}
          <TabPanel value={tabValue} index={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" component="div" gutterBottom>
                Forecast de Custos - Itens do Checklist
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Esta tabela apresenta todos os itens do checklist com seus respectivos valores.
              </Typography>
            </Box>

            {serviceRequest.checklist && serviceRequest.checklist.length > 0 ? (
              <Paper elevation={1}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Descrição do Item</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell align="right">Valor Coletado</TableCell>
                        <TableCell align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {serviceRequest.checklist.map((item: any, index: number) => (
                        <TableRow key={item._id || index}>
                          <TableCell>{item.title || item.description}</TableCell>
                          <TableCell>{getItemTypeLabel(item.type)}</TableCell>
                          <TableCell align="right">
                            {item.value ? costService.formatCurrency(Number(item.value) || 0) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {item.collectedValue ? (
                              <Typography variant="body2" color="primary" fontWeight="medium">
                                {item.collectedValue}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={item.isCompleted ? 'Concluído' : 'Pendente'}
                              color={item.isCompleted ? 'success' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={2} align="right">
                          <Typography variant="subtitle1" fontWeight="bold">
                            Total:
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle1" fontWeight="bold">
                            {costService.formatCurrency(
                              serviceRequest.checklist
                                .reduce((total: number, item: any) => total + (Number(item.value) || 0), 0)
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell colSpan={2} />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Paper>
            ) : (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                  <AssignmentIcon color="disabled" sx={{ fontSize: 60, mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Nenhum item no checklist
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 500 }}>
                    Este chamado técnico ainda não possui itens no checklist. Adicione itens na aba Checklist para visualizar o forecast de custo.
                  </Typography>
                </Box>
              </Paper>
            )}
          </TabPanel>
        </Box>

        {/* Confirm Delete Dialog */}
        <Dialog
          open={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
        >
          <DialogTitle>Cancelar Chamado Técnico</DialogTitle>
          <DialogContent>
            <Typography>
              Tem certeza que deseja cancelar este chamado técnico? Esta ação não pode ser desfeita.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)}>Não</Button>
            <Button onClick={handleConfirmDelete} color="error" autoFocus>
              Sim, Cancelar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Action Dialog */}
        <Dialog
          open={actionDialogOpen}
          onClose={() => setActionDialogOpen(false)}
        >
          <DialogTitle>Adicionar Ação</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="action"
              label="Descrição da Ação"
              type="text"
              fullWidth
              multiline
              rows={4}
              value={actionText}
              onChange={(e) => setActionText(e.target.value)}
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="action-status-label">Tipo</InputLabel>
              <Select
                labelId="action-status-label"
                value={actionStatus}
                label="Tipo"
                onChange={(e) => setActionStatus(e.target.value as any)}
              >
                <MenuItem value="info">Informação</MenuItem>
                <MenuItem value="warning">Aviso</MenuItem>
                <MenuItem value="error">Erro</MenuItem>
                <MenuItem value="success">Sucesso</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActionDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleAddAction}
              color="primary"
              disabled={!actionText.trim() || addingAction}
            >
              {addingAction ? <CircularProgress size={24} /> : 'Adicionar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Diálogo de confirmação para carregar template de checklist */}
        <Dialog
          open={confirmTemplateDialogOpen}
          onClose={() => setConfirmTemplateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Carregar Template de Checklist</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Encontramos um template de checklist para o tipo de chamado <strong>{serviceRequest?.type && 
              // @ts-ignore - Tipo any não pode ser usado para indexar
              serviceTypeLabels[serviceRequest.type] || serviceRequest?.type}</strong>.
              <br />
              Deseja carregar os itens automaticamente? Isso irá adicionar todos os itens do template ao checklist deste chamado.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmTemplateDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={() => {
                setConfirmTemplateDialogOpen(false);
                applyChecklistTemplate();
              }}
              color="primary"
              variant="contained"
              autoFocus
            >
              Carregar Template
            </Button>
          </DialogActions>
        </Dialog>

        {/* O diálogo para adicionar item ao checklist foi removido conforme requisito */}
      </Box>
    </AuthLayout>
  );
};

export default ServiceRequestDetail;
